import React, { useState, useEffect } from 'react';
import { Row, Col, Alert, Spin } from 'antd';
import { WalletIcon } from 'lucide-react';
import TransferForm from '../component/fund-transfer/TransferForm';
import TransferHistory from '../component/fund-transfer/TransferHistory';
import EmployeeFundService, {
  FundTransferResponse,
} from '../service/employee-service/Employee-fund.service';
import { EmployeeFundResponse } from '../model/EmployeeFund.model';
import useUserStorage from '../hook/useUserStorage';
import useEmployee from '../hook/useEmployee';

const FundTransfer: React.FC = () => {
  const { user } = useUserStorage();
  const { employee } = useEmployee();
  const [employeeFund, setEmployeeFund] = useState<EmployeeFundResponse | null>(
    null
  );
  const [transfers, setTransfers] = useState<FundTransferResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!employee?.id) {
      setError('Không tìm thấy thông tin nhân viên');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch employee fund info
      const fundData = await EmployeeFundService.getFundByEmployeeId(
        employee.id
      );
      setEmployeeFund(fundData);

      // Fetch transfer history
      const transferData = await EmployeeFundService.getTransfersByEmployeeId(
        employee.id
      );
      setTransfers(transferData);
    } catch (error: any) {
      console.error('Error fetching fund transfer data:', error);
      setError(error.response?.data || 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employee?.id) {
      fetchData();
    }
  }, [employee?.id]);

  const handleTransferSuccess = () => {
    fetchData(); // Refresh both fund info and transfer history
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Lỗi"
        description={error}
        type="error"
        showIcon
        className="mb-6"
      />
    );
  }

  if (!employeeFund) {
    return (
      <Alert
        message="Không tìm thấy thông tin quỹ"
        description="Không thể tải thông tin quỹ nhân viên. Vui lòng liên hệ quản trị viên."
        type="warning"
        showIcon
        className="mb-6"
      />
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <WalletIcon className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800">
            Chuyển tiền về công ty
          </h1>
        </div>
        <p className="text-gray-600">
          Quản lý việc chuyển tiền từ tài khoản cá nhân về tài khoản công ty
        </p>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <TransferForm
            employeeId={employee?.id || 0}
            currentBalance={employeeFund.currentBalance}
            onTransferSuccess={handleTransferSuccess}
          />
        </Col>

        <Col xs={24} lg={16}>
          <TransferHistory
            transfers={transfers}
            onTransferUpdate={handleTransferSuccess}
            currentBalance={employeeFund.currentBalance}
            employeeId={employee?.id || 0}
          />
        </Col>
      </Row>
    </div>
  );
};

export default FundTransfer;
